import React, { useEffect, useRef, useState } from 'react';
import {
    IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton,
    IonContent, IonFooter, IonIcon, IonText, IonSpinner, IonLabel, IonTextarea,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { apiService } from '@/services/ApiService';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import { OnlineUserResponse } from '@/types/response/enterpriseResponses';

interface SendMessageModalProps {
    isOpen: boolean;
    onDidDismiss: () => void;
    onlineUsers: OnlineUserResponse[];
    selectedUserIds: number[];
}

const SendMessageModal: React.FC<SendMessageModalProps> = ({
    isOpen, onDidDismiss, onlineUsers, selectedUserIds,
}) => {
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [toast, setToast] = useState<{ message: string; variant: ToasterVariant } | null>(null);
    const forceClosingRef = useRef(false);

    useEffect(() => {
        if (isOpen) {
            forceClosingRef.current = false;
            setMessage('');
            setError('');
        }
    }, [isOpen]);

    const selectedUsers = onlineUsers.filter(u => selectedUserIds.includes(u.Id));

    const handleSend = async () => {
        if (!message.trim()) { setError('Message is required.'); return; }
        if (selectedUserIds.length === 0) { setError('At least one signed-in user must be selected.'); return; }
        setIsSaving(true);
        try {
            await apiService.sendMessage({ Text: message, Users: selectedUserIds });
            setToast({ message: 'Message sent.', variant: 'success' });
            forceClosingRef.current = true;
            onDidDismiss();
        } catch {
            setToast({ message: 'Failed to send message.', variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <IonModal isOpen={isOpen} onDidDismiss={() => { if (!forceClosingRef.current) onDidDismiss(); }}
                style={{ '--width': '500px', '--height': '360px', '--border-radius': '8px' }}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle style={{ fontSize: '1rem' }}>Send Message</IonTitle>
                        <IonButtons slot="end">
                            <IonButton onClick={onDidDismiss}><IonIcon icon={closeOutline} /></IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>

                <IonContent style={{ '--padding-start': '1.25rem', '--padding-end': '1.25rem', '--padding-top': '1rem' }}>
                    {isSaving ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><IonSpinner /></div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <div>
                                <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>
                                    Recipients ({selectedUsers.length})
                                </IonLabel>
                                <IonText style={{ fontSize: '0.9rem' }}>
                                    {selectedUsers.length === 0
                                        ? <IonText color="danger">No users selected — go back and select users first.</IonText>
                                        : selectedUsers.map(u => `${u.FirstName} ${u.LastName}`).join(', ')
                                    }
                                </IonText>
                            </div>
                            <div>
                                <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>Message *</IonLabel>
                                <IonTextarea
                                    value={message}
                                    onIonInput={e => { setMessage(e.detail.value ?? ''); setError(''); }}
                                    rows={4}
                                    style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }}
                                />
                                {error && <IonText color="danger" style={{ fontSize: '0.75rem' }}>{error}</IonText>}
                            </div>
                        </div>
                    )}
                </IonContent>

                {!isSaving && (
                    <IonFooter>
                        <IonToolbar style={{ '--padding-start': '1rem', '--padding-end': '1rem' }}>
                            <IonButtons slot="start">
                                <IonButton color="medium" onClick={onDidDismiss}>Cancel</IonButton>
                            </IonButtons>
                            <IonButtons slot="end">
                                <IonButton fill="solid" color="primary" disabled={selectedUserIds.length === 0} onClick={handleSend}>Send</IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonFooter>
                )}
            </IonModal>

            <Toaster isOpen={!!toast} message={toast?.message} variant={toast?.variant} onDidDismiss={() => setToast(null)} />
        </>
    );
};

export default SendMessageModal;
